import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

const ROLE = { driver: 'Fahrer', customer: 'Kunde', partner: 'Partner' };

export default function SupportStartConversation({ open, onClose, onStarted }) {
  const [role, setRole] = useState('driver');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const [more, setMore] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const generation = useRef(0);
  const sending = useRef(false);
  const submitted = useRef(null);
  const uncertain = useRef(false);
  const heading = useRef(null);

  useEffect(() => { if (open) heading.current?.focus(); }, [open]);
  useEffect(() => () => { generation.current += 1; }, []);

  const invalidateSearch = () => {
    generation.current += 1;
    setResults([]); setRecipient(null); setSearched(false); setSearching(false); setMore(false); setError('');
  };
  const search = async () => {
    if (query.trim().length < 2 || pending || busy) return;
    const current = ++generation.current;
    setSearching(true); setRecipient(null); setResults([]); setError('');
    try {
      const { data } = await api.get('/admin/support-inbox/recipients', { params: { role, q: query.trim() } });
      if (current !== generation.current) return;
      setResults(data.recipients || []); setMore(Boolean(data.has_more)); setSearched(true);
    } catch {
      if (current === generation.current) setError('Empfänger konnten nicht geladen werden. Bitte erneut suchen.');
    } finally {
      if (current === generation.current) setSearching(false);
    }
  };
  const send = async (event) => {
    event.preventDefault();
    if (sending.current || !recipient || !subject.trim() || !message.trim()) return;
    sending.current = true; setBusy(true); setError('');
    try {
      // Freeze the exact recipient/text and retry key before the first request.
      // A timeout cannot silently turn a retry into a second message or a different recipient.
      if (!submitted.current) submitted.current = {
        request_id: window.crypto.randomUUID(), user_id: recipient.id, user_type: recipient.role,
        subject: subject.trim(), message: message.trim(),
      };
      setPending(true);
      const { data } = await api.post('/admin/support-inbox/conversations', submitted.current);
      if (!data.conversation_id) throw new Error('Missing conversation');
      const title = submitted.current.subject;
      submitted.current = null; uncertain.current = false; setPending(false); setRecipient(null); setResults([]);
      setQuery(''); setSubject(''); setMessage(''); setSearched(false);
      onStarted({ kind: 'chat', id: data.conversation_id, title });
    } catch (err) {
      const status = err.response?.status;
      if (status >= 400 && status < 500 && !uncertain.current) {
        submitted.current = null; setPending(false);
        setError(err.response?.data?.error || 'Gespräch wurde nicht gestartet. Bitte Angaben prüfen.');
      } else {
        uncertain.current = true;
        setError('Ergebnis unklar. „Erneut senden“ prüft dieselbe Nachricht und legt sie nicht doppelt an.');
      }
    } finally {
      sending.current = false; setBusy(false);
    }
  };

  // Keep an uncertain request when the panel is closed and opened again.
  if (!open) return null;
  const locked = busy || pending;
  return (
    <section className="support-start" aria-labelledby="support-start-title">
      <div className="support-start-heading">
        <h2 id="support-start-title" tabIndex={-1} ref={heading}>Gespräch starten</h2>
        <button type="button" onClick={onClose} disabled={busy}>Schließen</button>
      </div>
      <p>Wähle genau ein Konto aus. Die Nachricht erscheint im Supportpostfach; der Empfänger kann dort antworten.</p>
      <div className="support-start-search">
        <label>Kontoart<select aria-label="Kontoart" value={role} disabled={locked}
          onChange={(e) => { setRole(e.target.value); invalidateSearch(); }}>
          {Object.entries(ROLE).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select></label>
        <label>Empfänger suchen<input value={query} disabled={locked} maxLength={100}
          placeholder="Name, E-Mail, Telefon oder Konto-ID"
          onChange={(e) => { setQuery(e.target.value); invalidateSearch(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }} /></label>
        <button type="button" onClick={search} disabled={locked || searching || query.trim().length < 2}>
          {searching ? 'Sucht …' : 'Empfänger suchen'}
        </button>
      </div>
      {searched && results.length === 0 && <p role="status">Kein passendes Konto gefunden.</p>}
      {more && <p role="status">Mehr als 20 Treffer. Bitte die Suche eingrenzen.</p>}
      {results.length > 0 && <div className="support-start-results" aria-label="Empfängerauswahl">
        {results.map((person) => <button type="button" key={person.id} disabled={locked}
          aria-pressed={recipient?.id === person.id} onClick={() => setRecipient(person)}>
          <strong>{person.name} · {ROLE[person.role]}</strong>
          <span>{[person.company_name, person.email, person.phone].filter(Boolean).join(' · ')}</span>
          <small>Konto: {person.id}</small>
        </button>)}
      </div>}
      <form onSubmit={send}>
        {recipient && <p className="support-start-recipient">An: <strong>{recipient.name} · {ROLE[recipient.role]}</strong> · {recipient.email} · Konto {recipient.id}</p>}
        <label>Betreff<input required maxLength={160} value={subject} disabled={locked}
          onChange={(e) => setSubject(e.target.value)} placeholder="Zum Beispiel: Unterlagen vervollständigen" /></label>
        <label>Nachricht<textarea required maxLength={4000} rows={4} value={message} disabled={locked}
          onChange={(e) => setMessage(e.target.value)} placeholder="Deine Nachricht an das ausgewählte Konto …" /></label>
        {error && <p role="alert" className="support-error">{error}</p>}
        <button type="submit" disabled={busy || !recipient || !subject.trim() || !message.trim()}>
          {busy ? 'Wird gesendet …' : pending ? 'Erneut senden' : 'Gespräch starten und senden'}
        </button>
      </form>
    </section>
  );
}
